import { createFileRoute, Navigate, Outlet, redirect } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import type { RouterContext } from "~/router";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useTenant } from "~/hooks/useTenant";
import { Spinner } from "~/components/ui/spinner";
import { EmailVerificationGate } from "~/components/email-verification-gate";
import type { AuthUser } from "~/hooks/useAuth0Login";

export const Route = createFileRoute("/$tenantSlug/_protected")({
  beforeLoad: async ({ context, location, params }) => {
    const { auth } = context as RouterContext;
    if (!auth.isLoading && !auth.isAuthenticated) {
      const searchParams = new URLSearchParams(location.search);
      const invitation = searchParams.get("invitation");
      const organization = searchParams.get("organization");
      const organization_name = searchParams.get("organization_name");

      throw redirect({
        to: `/${params.tenantSlug}/login` as any,
        search: {
          ...(invitation && { invitation }),
          ...(organization && { organization }),
          ...(organization_name && { organization_name }),
        } as any,
      });
    }
  },
  component: TenantProtectedLayout,
});

function TenantProtectedLayout() {
  const {
    isUniversityAccount,
    isAuthenticated,
    organizationSlug,
    isLoading,
    user,
  } = useAuthenticatedUser();
  const { user: auth0User } = useAuth0<AuthUser>();
  const tenant = useTenant();
  const { tenantSlug } = Route.useParams();

  const loadingView = (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Spinner className="size-8 text-brand-green-primary" />
    </div>
  );

  if (isLoading || tenant.isLoading) {
    return loadingView;
  }

  if (!isAuthenticated) {
    return <Navigate to={`/${tenantSlug}/login` as any} replace />;
  }

  // Guard against the window between isLoading=false and data being populated
  if (!user) {
    return loadingView;
  }

  // Block access until email is verified
  if (auth0User && !auth0User.email_verified) {
    return <EmailVerificationGate />;
  }

  // Redirect non-university accounts away from tenant portals
  if (!isUniversityAccount) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect university users who belong to a different org
  if (organizationSlug && organizationSlug !== tenantSlug) {
    return <Navigate to={`/${organizationSlug}/dashboard` as any} replace />;
  }

  return <Outlet />;
}
